import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

/**
 * CountBadge — a small numeric count that overlays a control (icon button, tab, avatar…).
 *
 * Reusable primitive: the candidate-list filter uses it for the active-filter count, but it's
 * deliberately generic (notifications, unread counts, etc.). It does NOT position itself — the
 * host anchors it (see DropdownMenu's `badge` slot, which pins it top-right of the toggle).
 *
 * Rules baked in:
 *   • Renders nothing at count <= 0 (so callers can pass the raw number unconditionally).
 *   • Caps at `max` (default 9) → "9+", so it never blows out the pill width.
 *   • Tabular figures, so 1- vs 2-glyph counts don't jiggle the layout.
 */
export interface CountBadgeProps {
  count: number;
  max?: number;
  className?: string;
}

const CountBadge: React.FunctionComponent<CountBadgeProps> = ({ count, max = 9, className }) => {
  if (!count || count <= 0) return null;
  const label = count > max ? `${max}+` : String(count);
  return (
    <Styled.Badge className={className} aria-hidden="true">
      {label}
    </Styled.Badge>
  );
};

export default CountBadge;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Badge = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: CountBadge;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    min-width: 15px;
    height: 15px;
    padding: 0 4px;
    border-radius: 999px;
    background: ${t.dark ? t.color.gray[200] : t.color.black};
    color: ${t.dark ? t.color.gray[900] : t.color.white};
    font-size: 9px;
    line-height: 1;
    ${t.text.bold}
    font-variant-numeric: tabular-nums;
    box-shadow: 0 0 0 1.5px ${t.dark ? t.color.gray[900] : t.color.white};
  `;
});
